import { Command as CommandPrimitive } from 'cmdk';
import { MapPin, Train } from 'lucide-react';
import type { LngLatLike } from 'mapbox-gl';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/atoms/command';
import { Input } from '@/components/atoms/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/atoms/popover';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  fetchSearchRetrieve,
  fetchSearchSuggestions,
  getMapboxAccessToken,
  groupSuggestions,
  MAPBOX_CONFIG,
} from '@/features/mapbox';
import { cn } from '@/libs/utils/cn';
import { parseAnyCoordinates } from '@/libs/utils/coordinates';
import type { SearchBoxFeatureSuggestion, SearchBoxSuggestion } from '@/types/mapbox';

type Props = {
  accessToken?: string; // optional, otherwise read env
  proximity?: LngLatLike; // [lng, lat] to bias results
  placeholder?: string;
  onSelect?: (value: SearchBoxFeatureSuggestion | LngLatLike) => void; // passes the retrieve feature
  debounceMs?: number;
  className?: string;
  disabled?: boolean;
  /** Extra decoration rendered inside the input, on the left. Domain-agnostic — the caller owns its content. */
  leftSlot?: ReactNode;
  /**
   * Extra results rendered above the address results. Domain-agnostic — the
   * caller owns its content and receives the live query plus a `close` to
   * dismiss the popover once it has handled a pick of its own.
   */
  resultsSlot?: (context: { query: string; close: () => void }) => ReactNode;
  /** Fires when the results popover opens or closes. */
  onOpenChange?: (open: boolean) => void;
};

export function MapboxSearch({
  accessToken,
  proximity,
  placeholder = 'Search places, addresses, POIs...',
  onSelect,
  debounceMs = MAPBOX_CONFIG.DEFAULT_DEBOUNCE_MS,
  className,
  leftSlot,
  resultsSlot,
  disabled = false,
  onOpenChange,
}: Props) {
  const token = accessToken ?? getMapboxAccessToken();

  const [open, setOpen] = useState(false);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    onOpenChangeRef.current?.(open);
  }, [open]);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchBoxSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const sessionToken = useRef<string>(crypto.randomUUID());
  const gpsSearch = useRef<{ latitude: number | null; longitude: number | null }>({
    latitude: null,
    longitude: null,
  });

  function hasGpsCoordinates(value: {
    latitude: number | null;
    longitude: number | null;
  }): value is { latitude: number; longitude: number } {
    return value.latitude !== null && value.longitude !== null;
  }

  // Debounced fetch suggest
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh conditionned to selected values
  useEffect(() => {
    if (!query || query.trim().length < 1) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(async () => {
      // abort previous
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      const signal = abortRef.current.signal;

      // detect if query is gps coordinates and act accordingly
      const detectedCoordinates = parseAnyCoordinates(query);
      if (detectedCoordinates) {
        gpsSearch.current = {
          latitude: detectedCoordinates.lat,
          longitude: detectedCoordinates.lng,
        };
        setSuggestions([]);
        setLoading(false);
        return;
      }

      // If no valid coordinates detected, clear the ref
      gpsSearch.current = { latitude: null, longitude: null };

      try {
        const suggestions = await fetchSearchSuggestions(
          {
            query,
            sessionToken: sessionToken.current,
            accessToken: token,
          },
          signal,
        );

        setSuggestions(suggestions);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [query, token, proximity, debounceMs]);

  // When a suggestion is chosen -> call /retrieve to get coordinates & features
  async function handleSelect(s: SearchBoxSuggestion) {
    setSuggestions([]);
    setQuery(s.name ?? '');

    const mapboxId = s.mapbox_id ?? '';
    if (!mapboxId) {
      toast.warning('No Mapbox ID for given location');
      // Nothing to retrieve — call onSelect with suggestion
      return;
    }

    const response = await fetchSearchRetrieve({
      mapboxId,
      sessionToken: sessionToken.current,
      accessToken: token,
    });

    if (!response) return;

    // retrieve returns features array — pick first
    const feature = response.features && response.features.length > 0 ? response.features[0] : null;
    if (!feature) {
      toast.error('Mapbox location feature not found');
      return;
    }

    onSelect?.(feature);
    // Close the popover input to hide autocomplete
    setOpen(false);
  }

  const grouped = useMemo(() => groupSuggestions(suggestions), [suggestions]);

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <Command shouldFilter={false} className="overflow-visible">
          <PopoverAnchor asChild>
            <div className="relative">
              <CommandPrimitive.Input
                asChild
                value={query}
                onValueChange={(value) => {
                  setQuery(value);
                  setOpen(() => !!value.trim());
                }}
                placeholder={placeholder}
                aria-label="Search locations"
                onKeyDown={(e) =>
                  setOpen(() => {
                    if (e.key === 'Escape') return false;
                    if (suggestions.length > 0 || !!query) return true;
                    return false;
                  })
                }
                onMouseDown={() =>
                  setOpen((open) => {
                    if (suggestions.length <= 0) return false;
                    return !!query || !open;
                  })
                }
                onFocus={() =>
                  setOpen(() => {
                    if (suggestions.length > 0 || !!query) return true;
                    return false;
                  })
                }
                className={cn(
                  'w-full rounded-[10px] border-border shadow-modal',
                  leftSlot != null ? 'pl-[49px]' : 'pl-3',
                )}
                disabled={disabled}
              >
                <Input type="search" />
              </CommandPrimitive.Input>
              {leftSlot != null && (
                <div className="absolute left-3 top-1/2 -translate-y-1/2">{leftSlot}</div>
              )}
            </div>
          </PopoverAnchor>
          <PopoverContent
            asChild
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (e.target instanceof Element && e.target.hasAttribute('cmdk-input')) {
                e.preventDefault();
              }
            }}
            className="w-radix-popover-trigger rounded-[10px] border-border p-0 shadow-modal"
          >
            <CommandList className="py-2.5 px-3">
              {resultsSlot?.({ query, close: () => setOpen(false) })}
              {loading && (
                <CommandPrimitive.Loading>
                  <div className="p-1">
                    <Skeleton className="h-6 w-full" />
                  </div>
                </CommandPrimitive.Loading>
              )}
              {query &&
                query.trim().length > 1 &&
                suggestions.length <= 0 &&
                hasGpsCoordinates(gpsSearch.current) && (
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        onSelect?.([gpsSearch.current.longitude!, gpsSearch.current.latitude!]);
                        // Close the popover input to hide autocomplete
                        setOpen(false);
                      }}
                      className="cursor-pointer hover:!bg-brand-150 data-[selected=true]:bg-brand-150"
                    >
                      <MapPin className="size-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <div className="text-sm font-medium">{query}</div>
                        <div className="text-xs opacity-70"></div>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                )}
              {grouped.orderedGroupKeys.map((groupKey) => {
                const items = grouped.groups[groupKey];
                if (!items || items.length === 0) return null;
                const Icon = groupKey === 'Train stations' ? Train : MapPin;
                return (
                  <CommandGroup key={groupKey} className="pb-1">
                    <span className="text-sm text-primary-500">{groupKey}</span>
                    {items.map((s, idx) => (
                      <CommandItem
                        id={`suggestion-${idx}`}
                        key={s.mapbox_id ?? `${groupKey}-${idx}`}
                        onSelect={() => handleSelect(s)}
                        value={`${s.name}-${s.mapbox_id}`}
                        className="cursor-pointer hover:!bg-brand-150 data-[selected=true]:bg-brand-150"
                      >
                        <Icon className="size-4 text-muted-foreground" />
                        <div className="flex flex-col">
                          <div className="text-sm font-medium">
                            {s.name ?? s.full_address ?? 'Unknown'}
                          </div>
                          <div className="text-xs opacity-70">{s.place_formatted ?? ''}</div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
            </CommandList>
          </PopoverContent>
        </Command>
      </Popover>
    </div>
  );
}
